/**
 * THE BUSINESS'S OWN RATING, ON THE CLIENT SIDE.
 *
 * The server (`business-rating`) pools the two kinds of review a customer of
 * this business can actually leave — a review of an appointment they
 * completed, and a review of a product they received — into one count-weighted
 * mean, and sends the per-source breakdown along with it. This file is the
 * client's half of that contract: the empty value, a tolerant reader, and the
 * sentence that tells the shopper what the number is made of.
 *
 * THE GOOGLE RATING IS NOT HERE AND MUST NOT BE. It arrives through
 * `api/googleReviews` and is drawn by `GoogleRatingButton` /
 * `GoogleReviewsSection`, with Google's own mark and attribution beside it.
 * Merging it into this number would present someone else's reviews as ours,
 * which Google's terms forbid and which would be a lie regardless of that.
 *
 * WHY THE BREAKDOWN SENTENCE LIVES HERE AND NOT IN A COMPONENT. Three surfaces
 * show this number — the shop's side panel, the pooled reviews sheet, and the
 * "visit the shop" card on a product page — and a rating that is explained
 * differently on each of them is not explained at all.
 */

export const EMPTY_HISTOGRAM = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

export const EMPTY_BUSINESS_RATING = {
  count: 0,
  average: 0,
  histogram: { ...EMPTY_HISTOGRAM },
  sources: { appointments: { count: 0, average: 0 }, products: { count: 0, average: 0 } },
};

const source = (raw) => ({
  count: Number(raw?.count) || 0,
  average: Number(raw?.average) || 0,
});

/**
 * Read whatever the API gave us into the shape the UI expects.
 *
 * The tolerance is not defensive habit: `getSalonReviewSummary` — the
 * APPOINTMENT-review endpoint the side panel used to read — answers
 * `{ count, average, histogram }` with no `sources`, and a cached client, a
 * stale bundle, or a rollback can still produce that. Reading it as "no
 * rating" would blank the panel; reading it as service reviews is what it
 * actually is.
 */
export function normaliseBusinessRating(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return EMPTY_BUSINESS_RATING;
  const count = Number(raw.count) || 0;
  const average = Number(raw.average) || 0;
  const histogram = raw.histogram && typeof raw.histogram === 'object'
    ? { ...EMPTY_HISTOGRAM, ...raw.histogram }
    : { ...EMPTY_HISTOGRAM };
  const sources = raw.sources && typeof raw.sources === 'object'
    ? {
        appointments: source(raw.sources.appointments),
        products: source(raw.sources.products),
      }
    // No breakdown in the payload: this is the old appointment-only summary.
    : { appointments: { count, average }, products: { count: 0, average: 0 } };
  return { count, average, histogram, sources };
}

/** `'both' | 'products' | 'services' | null` — which reviews built the number. */
export function ratingSourceKey(rating) {
  const r = normaliseBusinessRating(rating);
  const services = r.sources.appointments.count > 0;
  const products = r.sources.products.count > 0;
  if (services && products) return 'both';
  if (products) return 'products';
  if (services) return 'services';
  return null;
}

/**
 * The one sentence that makes the number legible.
 *
 * Empty string when there is no rating — a caller can render it
 * unconditionally and get nothing rather than an explanation of nothing.
 */
export function ratingSourceLine(rating, t) {
  const r = normaliseBusinessRating(rating);
  const key = ratingSourceKey(r);
  if (!key) return '';
  if (key === 'both') {
    return t('shop.ratingFromBoth', {
      defaultValue: 'From {{services}} service and {{products}} product reviews',
      services: r.sources.appointments.count,
      products: r.sources.products.count,
    });
  }
  if (key === 'products') {
    return t('shop.ratingFromProducts', {
      defaultValue: 'From {{count}} product reviews',
      count: r.sources.products.count,
    });
  }
  return t('shop.ratingFromServices', {
    defaultValue: 'From {{count}} service reviews',
    count: r.sources.appointments.count,
  });
}

/**
 * One decimal, always — `5` reads as `5.0`, the way every rating on every
 * shop page in the world reads. An empty string for "no rating", so a caller
 * cannot paint `0.0` beside a star and invent a review nobody left.
 */
export function formatRatingValue(average) {
  const n = Number(average);
  if (!Number.isFinite(n) || n <= 0) return '';
  return n.toFixed(1);
}

/**
 * THE COUNT BESIDE THE STAR, THE WAY THE REFERENCE WRITES IT.
 *
 * Measured on `shop.app/m/johnelliott` with the side menu open, at 414 and
 * 1440: the tag reads `4.7 ★ (1.1K)` — value, one 12px filled star, and the
 * count ABBREVIATED, in a 60 × 16px box. Ours printed the raw integer, which
 * is identical at 12 reviews and four characters wider at 1,100 — and the tag
 * sits on one line beside a business name that is already competing for it.
 *
 * `Intl.NumberFormat` with compact notation, in the READER's locale, because
 * the abbreviation is a word: English shortens to `1.1K`, Hebrew to `1.1 אלף`,
 * Arabic to `١٫١ ألف`. Hand-rolling `K` would print an English letter into an
 * Arabic pane. Where the engine has no compact notation the plain localised
 * number is correct, just longer — never a crash and never a blank.
 *
 * Small counts are left exactly as they are: `12` is `12` in every locale that
 * matters here, and a shop with nine reviews must not have them rounded away.
 */
export function formatRatingCount(count, language) {
  const n = Number(count);
  if (!Number.isFinite(n) || n <= 0) return '';
  try {
    return new Intl.NumberFormat(language || undefined, {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(n);
  } catch (_) {
    try {
      return new Intl.NumberFormat(language || undefined).format(n);
    } catch (__) {
      return String(n);
    }
  }
}
